import Checkbox from '@material-ui/core/Checkbox';
import TableCell from '@material-ui/core/TableCell';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IoIosInformationCircle } from 'react-icons/io';
import { useHistory } from 'react-router-dom';
import Swal from 'sweetalert2';
import Header from '../../components/Header';
import StandardContainer from '../../components/StandardContainer';
import TableChannel from '../../components/Table';
import { useToast } from '../../hooks/toast';
import api from '../../services/api';
import { ImportButton } from '../Banner/styles';
import {
  ContainerImportTasks,
  Content,
  LoadingOverlayContainer,
  ImportButtonCancel,
} from './styles';
import Pikaday from 'pikaday';
import moment from 'moment';
import { Box, Chip, Modal, Divider } from '@material-ui/core';
import Loading from '../../components/Loading';

interface Data {
  id: string;
  code: number;
  name: string;
  externalId: string;
  enableImportButton: boolean;
  importStatus: string;
  actions: string;
  image_url: string;
  priority: number;
  active: string;
}

interface IVideo {
  id: number;
  name: string;
  externalId: string;
  enableImportButton: boolean;
  importStatus: string;
  image_url: string;
  actions: string;
  priority: number;
  active: string;
}

interface IChannel {
  id: number;
  name: string;
  externalId: string;
  enableImportButton: boolean;
  importStatus: string;
  image_url: string;
  priority: number;
  active: string;
}

function createData(
  id: string,
  code: number,
  name: string,
  externalId: string,
  enableImportButton: boolean,
  importStatus: string,
  actions: string,
  image_url: string,
  active: string,
  priority?: number,
): Data {
  if (!priority) {
    priority = 0 as number;
  }
  return {
    id,
    code,
    name,
    externalId,
    actions,
    image_url,
    enableImportButton,
    importStatus,
    priority,
    active,
  };
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

const headCells: HeadCell[] = [
  { id: 'code', numeric: true, disablePadding: true, label: 'ID' },
  { id: 'externalId', numeric: false, disablePadding: false, label: 'YT ID' },
  { id: 'name', numeric: false, disablePadding: false, label: 'Nome' },
  { id: 'image_url', numeric: false, disablePadding: false, label: 'Capa' },
  { id: 'actions', numeric: false, disablePadding: false, label: 'Ações' },
  {
    id: 'priority',
    numeric: false,
    disablePadding: false,
    label: 'Prioridade',
  },
  {
    id: 'active',
    numeric: false,
    disablePadding: false,
    label: 'Ativo',
  },
  {
    id: 'importStatus',
    numeric: false,
    disablePadding: false,
    label: 'Importação',
  },
];

const ChannelList = () => {
  const [newsRows, setNewsRows] = useState<Data[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [dataImportTasks, setDataImportTasks] = useState<any>();
  const [loadingImport, setLoadingImport] = useState(false);
  const isMountedRef = useRef(true);
  const isRefreshingRef = useRef(false);

  let statusColor;

  const datepickerRef = useRef<Pikaday | null>(null);

  const { addToast } = useToast();

  const history = useHistory();

  const getChannelTask = useCallback(async (channelId: number) => {
    const responseTask = await api.get(`/videos/channel/${channelId}/tasks`);
    return responseTask.data;
  }, []);

  const getData = useCallback(async () => {
    if (isRefreshingRef.current) {
      return;
    }

    isRefreshingRef.current = true;

    try {
      const { data } = await api.get(`/videos/channel/all`);
      const channels: IVideo[] = await Promise.all(
        data.map(async (channel: IVideo): Promise<IVideo> => {
          const taskData = await getChannelTask(channel.id);
          const isImporting =
            taskData?.actual?.status === 'IN_PROGRESS';

          return {
            ...channel,
            enableImportButton: !isImporting,
            importStatus: isImporting
              ? 'Importação em execução'
              : 'Não está em execução',
          };
        }),
      );

      if (!isMountedRef.current) {
        return;
      }

      setNewsRows(
        channels.map((channel: IVideo) =>
          createData(
            channel.id.toString(),
            channel.id,
            channel.name,
            channel.externalId,
            channel.enableImportButton,
            channel.importStatus,
            channel.actions,
            channel.image_url,
            channel.active,
            channel.priority,
          ),
        ),
      );
    } catch (error) {
      console.error(error);
    } finally {
      isRefreshingRef.current = false;
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [getChannelTask]);

  useEffect(() => {
    isMountedRef.current = true;
    getData();

    const interval = setInterval(getData, 10 * 1000);

    return () => {
      isMountedRef.current = false;
      clearInterval(interval);
    };
  }, [getData]);

  // const newsRows = useMemo(() => {
  //   return rows.map(row => createData(row.id.toString(), row.id, row.description, row.externalId,
  //     row.title, row.channel, row.channelName, row.uploadDate, row.importDate, row.priority));
  // }, [rows]);

  const onUpload  = useCallback(
    row => {
      history.push('/channel/upload', { channelId: row.id });
    },
    [history],
  );

  const onEdit = useCallback(
    row => {
      history.push('/channel/:id', { id: row.code});
    },
    [history],
  );

  const handleChangePosition = useCallback(
    async row => {
      Swal.fire({
        title: `Deseja alterar a prioridade do canal  ${row.name}?`,
        text: 'A prioridade é utilizada para definir a ordem de exibição dos canais',
        icon: 'question',
        input: 'number',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        showLoaderOnConfirm: true,
        inputAttributes: {
          min: '0',
        },
        preConfirm: priority => {
          return api
            .patch(`/videos/channel/${row.id}/position`, { priority })
            .then(response => {
              addToast({
                type: 'success',
                title: 'Prioridade alterada com sucesso!',
              });
              getData();
            })
            .catch(error => {
              addToast({
                type: 'error',
                title: 'Erro ao alterar prioridade!',
                description:
                  'Ocorreu um erro ao alterar a prioridade do canal, tente novamente mais tarde.',
              });
            });
        },
      });
    },
    [addToast, getData],
  );

  const pollingTask = useCallback(
    async (channelId: number, startedAt: string) => {
      try {
        const taskData = await getChannelTask(channelId);
        const actual = taskData?.actual;
        const taskStartedAt = actual?.dateInit
          ? new Date(actual.dateInit).getTime()
          : 0;
        const importStartedAt = new Date(startedAt).getTime();
        const isNewTask = taskStartedAt >= importStartedAt;

        if (!isNewTask || actual?.status === 'IN_PROGRESS') {
          if (isMountedRef.current) {
            setTimeout(() => pollingTask(channelId, startedAt), 10 * 1000);
          }
          return;
        }

        await getData();

        const isSuccess = actual.status === 'DONE';
        await Swal.fire({
          title: isSuccess
            ? 'Importação finalizada'
            : 'Importação finalizada com erros',
          html: `<p>${buildMessage(actual)}</p>`,
          icon: isSuccess ? 'success' : 'error',
        });
      } catch (error) {
        console.error(error);
        if (isMountedRef.current) {
          setTimeout(() => pollingTask(channelId, startedAt), 10 * 1000);
        }
      }
    },
    [getChannelTask, getData],
  );

  const onImport = useCallback(
    async (row: any, dateFrom?: string) => {
      const { externalId } = row;
      const channelId = Number(row.code || row.id);

      try {
        const currentTask = await getChannelTask(channelId);
        if (currentTask?.actual?.status === 'IN_PROGRESS') {
          await Swal.fire({
            icon: 'info',
            title: 'Importação em execução',
            text: `A importação do canal ${row.name} já está em execução. Você pode clicar em OK; ela continuará em background.`,
            confirmButtonText: 'OK',
          });
          return;
        }

        const response = await api.post(`/videos/import`, {
          externalId,
          dateFrom,
        });
        const startedAt =
          response.data?.startedAt || new Date().toISOString();

        setNewsRows(currentRows =>
          currentRows.map(item =>
            item.code === channelId
              ? {
                  ...item,
                  enableImportButton: false,
                  importStatus: 'Importação em execução',
                }
              : item,
          ),
        );

        await Swal.fire({
          icon: 'info',
          title: 'Importação em execução',
          text: `A importação do canal ${row.name} foi iniciada e continuará em background.`,
          confirmButtonText: 'OK',
        });

        pollingTask(channelId, startedAt);
      } catch (error) {
        console.error(error);
        addToast({
          title: 'Erro na importação',
          type: 'error',
          description: `Não foi possível iniciar a importação do canal ${row.name}`,
        });
      }
    },
    [addToast, getChannelTask, pollingTask],
  );

  const buildMessage = (taskData: any) => {
    if (!taskData) {
      return '';
    }

    return `
    <p>Total de videos importados: ${taskData.totalImportedVideos} </p>
    <p>Total de playlists importados: ${taskData.totalImportedPlaylists} </p>`;
  };

  const openModalToFireImport = useCallback(
    (row: any) => {
      Swal.fire({
        title: 'Importa a partir de qual data?',
        input: 'text',
        inputPlaceholder: 'Selecione a data',
        inputAttributes: {
          readonly: 'readonly',
        },
        showCloseButton: true,
        allowOutsideClick: true,

        preConfirm: () => {
          return datepickerRef.current?.getDate();
        },
        didOpen: () => {
          datepickerRef.current = new Pikaday({
            field: Swal.getInput(),
            maxDate: moment().startOf('day').subtract(1, 'days').toDate(),
            format: 'DD/MM/YYYY',
          });
        },
        didClose: () => {
          datepickerRef.current?.destroy();
          datepickerRef.current = null;
        },
      }).then(result => {
        if (!result.isConfirmed) {
          setLoadingImport(false);
          return;
        }

        setLoadingImport(true);
        const date = result.value;

        const dateUtc = date
          ? moment(date).startOf('day').utc().toISOString()
          : undefined;
        onImport(row, dateUtc)
          .then(() => {
            setLoadingImport(false);
          })
          .catch(error => {
            setLoadingImport(false);
            console.log(error, 'error');
          });
      });
    },
    [onImport],
  );

  const openTaskModal = useCallback(
    async row => {
      const channelId = Number(row.code || row.id);
      const data = await getChannelTask(channelId);

      if (!data?.actual) {
        Swal.fire({
          title: 'Sem Dados de importação',
          text: `Não foi encontrado nenhum dado de importação para o canal ${row.name}`,
          cancelButtonColor: '#d33',
          confirmButtonText: 'Importar',
          showCancelButton: true,
        }).then(result => {
          if (result.isConfirmed) {
            openModalToFireImport(row);
          }
        });

        return;
      }

      const { actual, before } = data;
      const isRunning = actual.status === 'IN_PROGRESS';

      Swal.fire({
        title: `${row.name}`,
        html: `<p>${buildMessage(actual)}</p>
            ${
              before
                ? `
            </br>
            <b>Importação Anterior: </b>
            <p>${buildMessage(before)}</p>`
                : ''
            }
      `,
        showCancelButton: true,
        confirmButtonColor: isRunning ? '#AAAAAA' : '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Importar',
        cancelButtonText: 'Fechar',
      }).then(result => {
        if (result.isConfirmed && !isRunning) {
          openModalToFireImport(row);
        }

        if (result.isConfirmed && isRunning) {
          Swal.fire({
            icon: 'info',
            title: 'Importação em execução',
            text: `A importação do canal ${row.name} está em execução. Você pode clicar em OK; ela continuará em background.`,
            confirmButtonText: 'OK',
          });
        }
      });
    },
    [getChannelTask, openModalToFireImport],
  );

  const openImportsTaskModal = useCallback(
    async row => {
      const responseImportsTask = await api.get(
        `/videos/import-tasks/${row.id}`,
      );

      const { data } = responseImportsTask;

      if (!data) {
        Swal.fire({
          title: 'Sem Dados de importação',
          text: `Não foi encontrado nenhum dado de importação para o canal ${row.name}`,
          cancelButtonColor: '#d33',
          confirmButtonText: 'Importar',
          showCancelButton: true,
        }).then(result => {
          if (result.isConfirmed) {
            openModalToFireImport(row);
          }
        });

        return;
      }

      setShowModal(true);
      setDataImportTasks(data);
    },
    [openModalToFireImport],
  );

  const buttonChangeStatus = (
    index: number,
    taskId: string,
    taskStatus: string,
  ) => {
    console.log(taskId, 'taskid');
    console.log(taskStatus, 'taskStatus');
    api
      .put(`/videos/channel/task/${taskId}`, { status: taskStatus })
      .then(() => {
        //código que vc fez no front
        console.log(taskId, 'id');
        const updatedDataImportTasksChangeStatus = [...dataImportTasks];
        updatedDataImportTasksChangeStatus[index].status = 'ERROR'; // muda o status para 'ERROR'
        setDataImportTasks(updatedDataImportTasksChangeStatus); // atualiza o estado
      })
      .catch(error => {
        addToast({
          title: 'Erro ao atualizar status da tarefa',
          type: 'error',
          description: error.message,
        });
      });
  };

  const tableBodyCells = useCallback(
    (isItemSelected, labelId, row) => (
      <>
        <TableCell padding="checkbox">
          <Checkbox
            checked={isItemSelected}
            inputProps={{ 'aria-labelledby': labelId }}
          />
        </TableCell>
        <TableCell component="th" id={labelId} scope="row" padding="none">
          {row.id}
        </TableCell>
        <TableCell align="left">{row.externalId}</TableCell>
        <TableCell align="left">{row.name}</TableCell>
        <TableCell align="left">
          <a
            href={row.image_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#008FC3' }}
          >
            Abrir imagem
          </a>
        </TableCell>
        <TableCell align="left">
          <ImportButton onClick={() => openTaskModal(row)}>
            <IoIosInformationCircle />
          </ImportButton>
        </TableCell>
        {/* <TableCell align="left">{row.priority}</TableCell> */}
        <TableCell align="left">
          <div style={{ display: 'flex' }}>
            <ImportButton
              style={{ width: '40px' }}
              onClick={() => handleChangePosition(row)}
            >
              {row.priority}
            </ImportButton>
          </div>
        </TableCell>
        <TableCell align="left">{row.active}</TableCell>
        <TableCell align="left">{row.importStatus}</TableCell>
        {/* <TableCell align="left">{row.active} */}
        {/* <div style={{ marginTop: '15px' }}>
          
          <TableCell padding="checkbox">
            <CheckboxComponent
              name="checkedActive"
              checkboxName="Ativo"
              checked={isCheckedActive}
              onChecked={() => setIsCheckedActive(!isCheckedActive)}
            />
          </TableCell>
        </div> */}
        <TableCell align="left">
          <ImportButton onClick={() => openImportsTaskModal(row)}>
            Todas Importações
          </ImportButton>
        </TableCell>
        <TableCell align="left">
          <ImportButton
            onClick={() => {
              history.push({
                pathname: '/playlist',
                state: { channel: row.externalId },
              });
            }}
          >
            Playlists
          </ImportButton>
        </TableCell>
      </>
    ),
    [handleChangePosition, history, openImportsTaskModal, openTaskModal],
  );

  useEffect(() => {
    console.log(dataImportTasks);
  }, [dataImportTasks]);

  return (
    <>
      <link
        rel="stylesheet"
        type="text/css"
        href="https://cdn.jsdelivr.net/npm/pikaday/css/pikaday.css"
      ></link>
      <StandardContainer>
        <Header />
        <Content>
          <LoadingOverlayContainer>
            {loadingImport && <Loading />}
            <TableChannel<Data, HeadCell[]>
              title="Canais do Youtube"
              headCells={headCells}
              rows={newsRows.sort((a, b) => (a.priority > b.priority ? 1 : -1))}
              onUpload={onUpload}
              onEdit={onEdit}
              tableBodyCells={tableBodyCells}
              loading={loading}
            />
          </LoadingOverlayContainer>
        </Content>
        <Modal
          open={showModal}
          onClose={() => setShowModal(false)}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              width: '50%',
              height: '80%',
              overflow: 'auto',
              bgcolor: '#312E38',
              padding: '50px 100px',
            }}
          >
            {dataImportTasks &&
              dataImportTasks.map((task: any, index: number) => {
                const date_init = `${new Date(
                  task.date_init,
                ).toLocaleDateString()} - ${new Date(
                  task.date_init,
                ).toLocaleTimeString()}`;
                const date_end = `${new Date(
                  task.date_end,
                ).toLocaleDateString()} - ${new Date(
                  task.date_end,
                ).toLocaleTimeString()}`;

                // console.log(task.status)

                if (task.status === 'DONE') statusColor = '#4ead13';
                else if (task.status === 'ERROR') statusColor = '#a31e1c';
                else statusColor = '#a39b07';

                return (
                  <>
                    <ContainerImportTasks>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <h2>{task.channel_name}</h2>
                        <Chip
                          variant="outlined"
                          size="medium"
                          label={task.status}
                          style={{
                            color: statusColor,
                            borderColor: statusColor,
                            marginLeft: 10,
                          }}
                        />
                        {task.status !== 'DONE' && task.status !== 'ERROR' ? (
                          <ImportButtonCancel
                            onClick={() =>
                              buttonChangeStatus(index, task.id, task.status)
                            }
                          >
                            Cancelar
                          </ImportButtonCancel>
                        ) : (
                          ''
                        )}
                      </div>
                      <i>Ínicio: {date_init}</i> |{' '}
                      <i>Fim: {date_end ? date_end : 'Indefinida'}</i>
                      <br />
                      <b>
                        Playlists importadas: {task.total_playlists_imported}
                      </b>
                    </ContainerImportTasks>
                    <Divider />
                  </>
                );
              })}
          </Box>
        </Modal>
      </StandardContainer>
    </>
  );
};

export default ChannelList;
